'use client'

import { useProductListAnalyticsProvider } from 'product-list/context/ProductListAnalyticsProvider/useProductListAnalyticsProvider'
import { useViewSearchParam } from 'product-list/hooks/useViewSearchParam/useViewSearchParam'
import type { ProductListViewRef } from 'product-list/types/ProductsList'
import { View } from 'product-list/types/View'
import {
	getProductListState,
	type ProductListState,
} from 'product-list/utils/productListState'
import {
	createContext,
	type Dispatch,
	forwardRef,
	type ReactNode,
	type SetStateAction,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState,
} from 'react'

export interface ProductListContextType {
	view: View
	defaultView: View
	name: string
	count: number
	lastSlotId: string | undefined
	storedState?: ProductListState
	shouldRenderSlideshowAnimation: boolean
	slideshowAnimationRendered: boolean
	hasInspirationalPresentation: boolean
	setCount: Dispatch<SetStateAction<number>>
	setView: Dispatch<SetStateAction<View>>
	restoreStoredState: () => void
	resetCount: () => void
	setSlideshowAnimationRendered: Dispatch<SetStateAction<boolean>>
}

interface ProductListProviderProps {
	readonly defaultView: View
	readonly initialCount: number
	readonly name: string
	readonly shouldRenderSlideshowAnimation: boolean
	readonly hasInspirationalPresentation: boolean
	readonly children?: (view: View) => ReactNode
	readonly onViewChange?: (view: View) => void
}

const noop = () => {
	// Do nothing
}

const defaultContext: ProductListContextType = {
	count: 0,
	defaultView: View.Standard,
	lastSlotId: '',
	name: 'product-list',
	view: View.Standard,
	shouldRenderSlideshowAnimation: false,
	slideshowAnimationRendered: false,
	hasInspirationalPresentation: false,
	setView: noop,
	setCount: noop,
	restoreStoredState: noop,
	resetCount: noop,
	setSlideshowAnimationRendered: noop,
}

export const ProductListContext =
	createContext<ProductListContextType>(defaultContext)

export const ProductListProvider = forwardRef<
	ProductListViewRef,
	Readonly<ProductListProviderProps>
>((params, ref) => {
	const {
		defaultView,
		initialCount,
		name,
		shouldRenderSlideshowAnimation,
		hasInspirationalPresentation,
		children,
		onViewChange,
	} = params

	const { setLayoutView } = useProductListAnalyticsProvider()
	const [count, setCount] = useState(initialCount)
	const [slideshowAnimationRendered, setSlideshowAnimationRendered] =
		useState(false)

	const currentView = useViewSearchParam()
	const initialView = currentView ?? defaultView

	const [view, setView] = useState(initialView)
	const [lastSlotId, setLastSlotId] = useState<string>()

	const storedState = useMemo(() => getProductListState(name), [name])

	const resetCount = useCallback(() => {
		setCount(initialCount)
	}, [initialCount])

	const restoreStoredState = useCallback(() => {
		if (storedState) {
			setCount(storedState.count)
			setView(storedState.view)
			setLastSlotId(storedState.slotId)
		}
	}, [storedState])

	const contextState: ProductListContextType = useMemo<ProductListContextType>(
		() => ({
			slideshowAnimationRendered,
			count,
			defaultView,
			lastSlotId,
			name,
			shouldRenderSlideshowAnimation,
			hasInspirationalPresentation,
			storedState,
			view,
			setView,
			setCount,
			restoreStoredState,
			resetCount,
			setSlideshowAnimationRendered,
		}),
		[
			slideshowAnimationRendered,
			count,
			defaultView,
			lastSlotId,
			name,
			shouldRenderSlideshowAnimation,
			hasInspirationalPresentation,
			storedState,
			view,
			resetCount,
			restoreStoredState,
		]
	)

	useEffect(() => {
		setLayoutView?.(view)
	}, [view, setLayoutView])

	useEffect(() => {
		onViewChange?.(view)
	}, [onViewChange, view])

	useImperativeHandle(ref, () => ({ setView }), [])

	return (
		<ProductListContext.Provider value={contextState}>
			{children?.(view)}
		</ProductListContext.Provider>
	)
})
